import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Link, Stack, Typography } from "@mui/material";
import { FormatQuoteOutlined } from "@mui/icons-material";

import { useUIStore } from "../../stores/UIStore";
import {
  isProvenanceMessageHot,
  useMessageProvenance,
  useProvenanceDialogueId,
} from "../../services/ProvenanceService";
import ProvenanceExportButton from "./ProvenanceExportButton";
import { countProvenanceTiers } from "./provenanceCounts";

type ResponseSourcesSummaryProps = {
  /** The assistant message id this summary describes. */
  messageId?: string | null;
};

/**
 * One-line provenance summary rendered under an assistant response:
 *
 *   "{N} collections searched · {N} passages read · {N} cited"  · View Sources
 *
 * Clicking "View Sources" opens the right sidebar on the Sources tab, where
 * the full three-tier funnel ({@link ProvenancePanel}) is shown. Renders
 * nothing until there is provenance worth summarizing (spec §9.1: show what we
 * have), so responses with no retrieval get no clutter.
 */
const ResponseSourcesSummary = ({ messageId }: ResponseSourcesSummaryProps) => {
  const dialogueId = useProvenanceDialogueId();
  const setRightSidebar = useUIStore((s) => s.setRightSidebar);

  // Visibility gate: this summary mounts under EVERY assistant message, and
  // each message-scope fetch makes the backend re-walk the whole dialogue —
  // so eagerly fetching all N on dialogue open is O(N^2) backend work (the
  // mount-side twin of the audit-8 polling request bomb). Fetch only once the
  // summary scrolls near the viewport (one-shot; React Query's staleTime
  // dedupes after that). The hot message — the turn that just streamed — stays
  // eager so the funnel fills in without a scroll nudge.
  const inViewRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (inView) return undefined;
    const node = inViewRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      // Test / legacy environments: no observer, keep the pre-gate behavior.
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setInView(true);
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView]);

  const { data: provenance } = useMessageProvenance(dialogueId, messageId, {
    enabled: inView || isProvenanceMessageHot(messageId ?? null),
  });

  const counts = useMemo(() => {
    if (!provenance) return null;
    // All three counts come from the SHARED tier-count helper — the single
    // definition the panel badges use — so this line and the panel can never
    // disagree (#20423 audit; the panel's Searched badge now counts distinct
    // collections too, matching this line's "N collections searched" copy).
    const { citedCount, readCount, searchedCollectionCount } =
      countProvenanceTiers(provenance);
    return {
      collectionsSearched: searchedCollectionCount,
      passagesRead: readCount,
      cited: citedCount,
    };
  }, [provenance]);

  // Nothing to summarize → render only the (zero-height) visibility sentinel:
  // the observer target has to exist before any data can load, and it keeps
  // watching so scrolling an unfetched summary into view triggers its fetch.
  if (
    !counts ||
    (counts.collectionsSearched === 0 &&
      counts.passagesRead === 0 &&
      counts.cited === 0)
  ) {
    return <Box ref={inViewRef} component="div" sx={{ height: 0 }} />;
  }

  const openSources = () =>
    setRightSidebar({
      selectedItem: "sources",
      isOpen: true,
      isArtifactItemOpen: false,
      currentArtifactId: null,
    });

  return (
    <Stack
      ref={inViewRef}
      direction="row"
      spacing={1}
      alignItems="center"
      data-testid="aa-response-sources-summary"
      sx={{ mt: 1, flexWrap: "wrap" }}
    >
      <FormatQuoteOutlined sx={{ fontSize: "1rem", color: "text.secondary" }} />
      <Typography variant="caption" color="text.secondary">
        {counts.collectionsSearched} collection
        {counts.collectionsSearched === 1 ? "" : "s"} searched ·{" "}
        {counts.passagesRead} passage
        {counts.passagesRead === 1 ? "" : "s"} read · {counts.cited} cited
      </Typography>
      <Box component="span" sx={{ color: "text.disabled" }}>
        ·
      </Box>
      <Link
        component="button"
        type="button"
        variant="caption"
        onClick={openSources}
        data-testid="aa-view-sources-link"
        data-attr="assistant:link:view-sources"
        sx={{ fontWeight: 600 }}
      >
        View Sources
      </Link>
      {/* Message-scoped audit export (spec Story 6), icon-only to keep the
          footer line compact. */}
      {dialogueId != null && messageId && (
        <ProvenanceExportButton
          dialogueId={dialogueId}
          messageId={messageId}
          compact
        />
      )}
    </Stack>
  );
};

export default ResponseSourcesSummary;
